import { useState, useEffect } from 'react';
import {
  AppBar,
  Box,
  Toolbar,
  IconButton,
  Typography,
  Menu,
  Button,
  MenuItem,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import MenuIcon from '@mui/icons-material/Menu';
import { Link as RouterLink, useLocation } from 'react-router-dom';

const pages = [
  { title: 'Home', path: '/' },
  { title: 'About', path: '/about' },
  { title: 'Services', path: '/services' },
  { title: 'Projects', path: '/projects' },
  { title: 'Case Studies', path: '/case-studies' },
  { title: 'Contact', path: '/contact' },
];

export default function Navigation() {
  const theme = useTheme();
  const [anchorElNav, setAnchorElNav] = useState<null | HTMLElement>(null);
  const [isScrolled, setIsScrolled] = useState(false);
  const location = useLocation();
  const isHomePage = location.pathname === '/';

  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY;
      setIsScrolled(scrollPosition > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleOpenNavMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorElNav(event.currentTarget);
  };

  const handleCloseNavMenu = () => {
    setAnchorElNav(null);
  };

  const getNavbarStyles = () => {
    if (!isHomePage) {
      return {
        backgroundColor: theme.palette.background.paper,
        boxShadow: '0 12px 40px rgba(15, 23, 42, 0.06)',
        color: theme.palette.text.primary,
        backdropFilter: 'blur(8px)',
      };
    }

    return {
      backgroundColor: isScrolled ? 'rgba(245, 245, 242, 0.98)' : 'transparent',
      boxShadow: isScrolled ? '0 16px 45px rgba(15, 23, 42, 0.08)' : 0,
      color: isScrolled ? theme.palette.text.primary : 'white',
      backdropFilter: isScrolled ? 'blur(8px)' : 'none',
    };
  };

  const navbarStyles = getNavbarStyles();

  const mobileMenuSx = {
    mt: 1.5,
    py: 0.5,
    '& .MuiPaper-root': {
      minWidth: 200,
      borderRadius: 1.5,
      boxShadow: theme.shadows[8],
      border: `1px solid ${theme.palette.divider}`,
    },
    '& .MuiMenuItem-root': {
      fontFamily: theme.typography.fontFamily,
      fontWeight: 500,
      letterSpacing: '0.16em',
      fontSize: 12,
      py: 1.5,
      justifyContent: 'center',
      '&:hover': { backgroundColor: 'rgba(15, 23, 42, 0.06)', color: theme.palette.primary.main },
    },
  };

  return (
    <AppBar 
      position="fixed" 
      sx={{
        ...navbarStyles,
        transition: 'all 0.3s ease-in-out',
        width: '100%',
        borderRadius: '0 0 20px 20px',
      }}
    >
      <Toolbar disableGutters sx={{ 
        width: '100%', 
        px: { xs: 2.5, md: 5 },
        '& .MuiButton-root': {
          fontFamily: '"Poppins", sans-serif',
          fontWeight: 500,
          letterSpacing: '0.16em',
          fontSize: 12,
        }
      }}>
        {/* Logo for desktop */}
        <Box
          component={RouterLink}
          to="/"
          sx={{
            mr: 2,
            display: { xs: 'none', md: 'flex' },
            alignItems: 'center'
          }}
        >
          <img src="/images/logos/main.png" alt="Scott Development Logo" height="40" />
        </Box>
        <Typography
          variant="h6"
          noWrap
          component={RouterLink}
          to="/"
          sx={{
            mr: 2,
            display: { xs: 'none', md: 'flex' },
            fontWeight: 700,
            letterSpacing: '0.22em',
            color: navbarStyles.color,
            textDecoration: 'none',
            transition: 'color 0.3s ease-in-out',
          }}
        >
          SCOTT DEVELOPMENT
        </Typography>

        {/* Mobile menu */}
        <Box sx={{ flexGrow: 1, display: { xs: 'flex', md: 'none' } }}>
          <IconButton
            size="large"
            aria-label="menu"
            aria-controls="menu-appbar"
            aria-haspopup="true"
            onClick={handleOpenNavMenu}
            sx={{ color: navbarStyles.color }}
          >
            <MenuIcon />
          </IconButton>
          <Menu
            id="menu-appbar"
            anchorEl={anchorElNav}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            open={Boolean(anchorElNav)}
            onClose={handleCloseNavMenu}
            keepMounted
            sx={mobileMenuSx}
          >
            {pages.map((page) => (
              <MenuItem
                key={page.title}
                onClick={handleCloseNavMenu}
                component={RouterLink}
                to={page.path}
              >
                {page.title}
              </MenuItem>
            ))}
          </Menu>
        </Box>

        {/* Logo for mobile */}
        <Typography
          variant="h6"
          noWrap
          component={RouterLink}
          to="/"
          sx={{
            mr: 2,
            display: { xs: 'flex', md: 'none' },
            flexGrow: 1,
            fontWeight: 700,
            color: navbarStyles.color,
            textDecoration: 'none',
            transition: 'color 0.3s ease-in-out',
          }}
        >
          SCOTT DEVELOPMENT
        </Typography>

        {/* Desktop menu */}
        <Box sx={{ flexGrow: 1, display: { xs: 'none', md: 'flex' }, justifyContent: 'flex-end', gap: 1 }}>
          {pages.map((page) => (
            <Button
              key={page.title}
              component={RouterLink}
              to={page.path}
              onClick={handleCloseNavMenu}
              sx={{ 
                my: 1.5,
                px: { md: 1.5, lg: 2.5 },
                color: navbarStyles.color,
                fontSize: { md: 10.5, lg: 12 },
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                minHeight: 40,
                transition: 'all 0.3s ease-in-out',
                borderRadius: 999,
                '&:hover': {
                  backgroundColor: 'rgba(15, 23, 42, 0.06)',
                  color: theme.palette.primary.main,
                  transform: 'translateY(-2px)',
                },
              }}
            >
              {page.title}
            </Button>
          ))}
        </Box>
      </Toolbar>
    </AppBar>
  );
} 